import React, { useState, useEffect, useCallback } from "react";
import { Form, Select, InputNumber, Typography } from "antd";

import axios from "axios";
import { useTranslation } from "react-i18next";
import { elvis } from "../../Utils";

const { Option, OptGroup } = Select;
const { Title } = Typography;

// Mirrors the server enum it.polimi.lode.model.building.NonOrdinarySubtype.
const NON_ORDINARY_SUBTYPES = [
    { value: "LARGE_SPAN",                label: "Large-span structure" },
    { value: "CULTURAL_HERITAGE_CHURCH",  label: "Church" },
    { value: "CULTURAL_HERITAGE_PALACE",  label: "Palace" },
    { value: "OTHER",                     label: "Other" },
];

const HERITAGE_TIERS = [
    { value: "WORLD_HERITAGE",   label: "World Heritage" },
    { value: "HAGUE_CONVENTION", label: "Hague Convention" },
    { value: "NATIONAL",         label: "National" },
    { value: "SUB_NATIONAL",     label: "Sub-national" },
    { value: "LOCAL",            label: "Local" },
];

/**
 * Element-panel version of the building identification form (BuildInfoForm):
 * same attributes, but read from / written to the component record so a
 * building can be registered as an element of any enterprise.
 */
const BuildingComponentForm = props => {
    const { t } = useTranslation();
    const [qualityTypes, setQualityTypes] = useState([]);
    const [conservationTypes, setConservationTypes] = useState([]);
    const [retrofittingTypes, setRetrofittingTypes] = useState([]);
    const [regularityTypes, setRegularityTypes] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [nonOrdinaryMaterialTypes, setNonOrdinaryMaterialTypes] = useState([]);
    const [positionTypes, setPositionTypes] = useState([]);
    const [groundFloorTypes, setGroundFloorTypes] = useState([]);
    const [usageTypes, setUsageTypes] = useState([]);
    const [foundationTypes, setFoundationTypes] = useState([]);
    const [mainUseTypes, setMainUseTypes] = useState([]);
    const [form] = Form.useForm();

    const isEditMode = props.isEditMode || false;
    const isCulturalHeritage = props.record.asset && props.record.asset.culturalHeritage === true;

    const formItemLayout = {
        labelCol: {
            sm: { span: 10 }
        },
        wrapperCol: {
            sm: { span: 14 }
        }
    };

    useEffect(() => {
        const fetchEnum = (path, setter) => {
            axios
                .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/" + path)
                .then(({ data }) => {
                    setter(data);
                })
                .catch(function (error) {
                    console.log(error);
                });
        };
        fetchEnum("qualities", setQualityTypes);
        fetchEnum("conservations", setConservationTypes);
        fetchEnum("retrofittings", setRetrofittingTypes);
        fetchEnum("regularities", setRegularityTypes);
        fetchEnum("materials", setMaterialTypes);
        fetchEnum("non-ordinary-materials", setNonOrdinaryMaterialTypes);
        fetchEnum("positions", setPositionTypes);
        fetchEnum("ground-floors", setGroundFloorTypes);
        fetchEnum("usages", setUsageTypes);
        fetchEnum("foundation-typologies", setFoundationTypes);
        fetchEnum("main-uses", setMainUseTypes);
    }, []);

    const setFieldsFromAsset = useCallback((asset) => {
        form.setFieldsValue({
            year: asset.year,
            quality: asset.quality,
            conservation: asset.conservation,
            retrofitting: asset.retrofitting,
            // A subtype implies non-ordinary: force the reveal open so the
            // subtype is visible and not cleared on save.
            regularity: asset.regularity || (asset.subtype ? 'NOT_REGULAR' : undefined),
            subtype: asset.subtype,
            usage: asset.usage,
            foundationTypology: asset.foundationTypology,
            inhabitants: asset.inhabitants,
            constructionCost: asset.constructionCost,
            economicValue: asset.economicValue,
            verticalMat: asset.verticalMat,
            horizontalMat: asset.horizontalMat,
            roofMat: asset.roofMat,
            buildPosition: asset.buildPosition,
            groundFloor: asset.groundFloor,
            nFloors: asset.nFloors,
            floorHeight: asset.floorHeight,
            nUnderFloors: asset.nUnderFloors,
            floorArea: asset.floorArea,
            mainUse: asset.mainUse,
            heritageTier: asset.heritageTier,
        });
    }, [form]);

    useEffect(() => {
        if (props.record.asset) {
            setFieldsFromAsset(props.record.asset);
        }
    }, [props.record.asset, setFieldsFromAsset]);

    useEffect(() => {
        if (!isEditMode && props.record.asset) {
            form.resetFields();
            setFieldsFromAsset(props.record.asset);
        }
    }, [isEditMode, form, props.record.asset, setFieldsFromAsset]);

    // BuildingMaterial is a flat enum; the two material families are rebuilt here as OptGroups
    // from the value-name prefix. Values matching no prefix render as standalone options.
    const materialFamilies = [
        { label: "Masonry", prefix: "MASONRY_" },
        { label: "Reinforced concrete", prefix: "RC_" },
    ];

    // Ordinary buildings keep the granular grouped material list; a non-ordinary
    // typology switches the same fields to the coarse list.
    const createMaterialSelect = (nonOrdinary) => {
        const renderOption = name => (
            <Option key={name} value={name}>
                {t('buildingEnums.material.' + name, name)}
            </Option>
        );
        if (nonOrdinary) {
            return nonOrdinaryMaterialTypes.map(renderOption);
        }
        const grouped = new Set();
        const groups = materialFamilies.map(family => {
            const options = materialTypes.filter(name => name.startsWith(family.prefix));
            options.forEach(name => grouped.add(name));
            return { label: family.label, options };
        });
        const standalone = materialTypes.filter(name => !grouped.has(name));
        return [
            ...groups
                .filter(group => group.options.length > 0)
                .map(group => (
                    <OptGroup key={group.label} label={group.label}>
                        {group.options.map(renderOption)}
                    </OptGroup>
                )),
            ...standalone.map(renderOption),
        ];
    }

    const onValuesChange = (changedValues) => {
        // Leaving the not-regular path clears the structure typology.
        if ('regularity' in changedValues && changedValues.regularity !== 'NOT_REGULAR') {
            form.setFieldsValue({ subtype: undefined });
        }
    };

    const handleFormChange = () => {
        if (!isEditMode) return;

        const values = form.getFieldsValue();
        const errors = form.getFieldsError();
        const isValid = errors.every(error => !error.errors.length);

        const newAsset = { ...props.record };
        newAsset.asset.principal = props.record.asset.principal === true;
        newAsset.asset.year = elvis(values.year);
        newAsset.asset.quality = elvis(values.quality);
        newAsset.asset.conservation = elvis(values.conservation);
        newAsset.asset.retrofitting = elvis(values.retrofitting);
        newAsset.asset.regularity = elvis(values.regularity);
        newAsset.asset.subtype = values.regularity === 'NOT_REGULAR' ? elvis(values.subtype) : null;
        newAsset.asset.usage = elvis(values.usage);
        newAsset.asset.foundationTypology = elvis(values.foundationTypology);
        newAsset.asset.inhabitants = elvis(values.inhabitants);
        newAsset.asset.constructionCost = elvis(values.constructionCost);
        newAsset.asset.economicValue = elvis(values.economicValue);
        newAsset.asset.verticalMat = elvis(values.verticalMat);
        newAsset.asset.horizontalMat = elvis(values.horizontalMat);
        newAsset.asset.roofMat = elvis(values.roofMat);
        newAsset.asset.buildPosition = elvis(values.buildPosition);
        newAsset.asset.groundFloor = elvis(values.groundFloor);
        newAsset.asset.nFloors = elvis(values.nFloors);
        newAsset.asset.floorHeight = elvis(values.floorHeight);
        newAsset.asset.nUnderFloors = elvis(values.nUnderFloors);
        newAsset.asset.floorArea = elvis(values.floorArea);
        newAsset.asset.mainUse = elvis(values.mainUse);
        newAsset.asset.heritageTier = isCulturalHeritage ? elvis(values.heritageTier) : null;
        newAsset.valid = isValid;
        props.parentCallback(newAsset);
    };

    const enumSelect = (types, i18nPrefix) => (
        <Select disabled={!isEditMode} allowClear>
            {types.map(name => (
                <Option key={name} value={name}>
                    {t(i18nPrefix + name, name)}
                </Option>
            ))}
        </Select>
    );

    return (
        <Form
            form={form}
            {...formItemLayout}
            className="details_form"
            onValuesChange={onValuesChange}
            onFieldsChange={handleFormChange}
        >
            <Title level={5}>Construction details</Title>
            <Form.Item label="Construction year" name="year">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            <Form.Item label="Quality" name="quality">
                {enumSelect(qualityTypes, 'buildingEnums.quality.')}
            </Form.Item>
            <Form.Item label="Conservation" name="conservation">
                {enumSelect(conservationTypes, 'buildingEnums.conservation.')}
            </Form.Item>
            <Form.Item label="Existing retrofitting measures" name="retrofitting">
                {enumSelect(retrofittingTypes, 'buildingEnums.retrofitting.')}
            </Form.Item>
            <Form.Item label="Usage" name="usage">
                {enumSelect(usageTypes, 'buildingEnums.usage.')}
            </Form.Item>
            <Form.Item label="Foundation typology" name="foundationTypology">
                {enumSelect(foundationTypes, 'buildingEnums.foundationTypology.')}
            </Form.Item>
            <Form.Item label="Regularity" name="regularity">
                {enumSelect(regularityTypes, 'buildingEnums.regularity.')}
            </Form.Item>
            <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.regularity !== curr.regularity}
            >
                {({ getFieldValue }) => getFieldValue('regularity') === 'NOT_REGULAR' ? (
                    // "Not regular" opens the possibility of declaring a special
                    // typology; leaving it empty keeps the building ordinary.
                    <Form.Item label="Structure typology" name="subtype">
                        <Select disabled={!isEditMode} allowClear placeholder="Ordinary (none)">
                            {NON_ORDINARY_SUBTYPES.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                ) : null}
            </Form.Item>

            <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.subtype !== curr.subtype || prev.regularity !== curr.regularity}
            >
                {({ getFieldValue }) => {
                    const nonOrdinary = getFieldValue('regularity') === 'NOT_REGULAR'
                        && !!getFieldValue('subtype');
                    return (
                        <>
                            <Title level={5}>Typology</Title>
                            <Form.Item label="Vertical structure material" name="verticalMat">
                                <Select disabled={!isEditMode} allowClear>
                                    {createMaterialSelect(nonOrdinary)}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Horizontal structure material" name="horizontalMat">
                                <Select disabled={!isEditMode} allowClear>
                                    {createMaterialSelect(nonOrdinary)}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Roof material" name="roofMat">
                                <Select disabled={!isEditMode} allowClear>
                                    {createMaterialSelect(nonOrdinary)}
                                </Select>
                            </Form.Item>
                        </>
                    );
                }}
            </Form.Item>

            <Title level={5}>Geometry</Title>
            <Form.Item label="Position" name="buildPosition">
                {enumSelect(positionTypes, 'buildingEnums.position.')}
            </Form.Item>
            <Form.Item label="Type of ground floor" name="groundFloor">
                {enumSelect(groundFloorTypes, 'buildingEnums.groundFloor.')}
            </Form.Item>
            <Form.Item label="N. of floors" name="nFloors">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            <Form.Item label="Average floor height (cm)" name="floorHeight">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            <Form.Item label="N. of underground floor" name="nUnderFloors">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            <Form.Item label="Average floor area (sm)" name="floorArea">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>

            <Title level={5}>Main use</Title>
            <Form.Item label="Main use" name="mainUse">
                {enumSelect(mainUseTypes, 'buildingEnums.mainUse.')}
            </Form.Item>

            <Title level={5}>Others</Title>
            <Form.Item label="N. of inhabitants" name="inhabitants">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            <Form.Item label="Construction cost (€/sm)" name="constructionCost">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            <Form.Item label="Economic value (€)" name="economicValue">
                <InputNumber disabled={!isEditMode} />
            </Form.Item>
            {isCulturalHeritage && (
                <Form.Item label="Listing" name="heritageTier">
                    <Select disabled={!isEditMode} allowClear>
                        {HERITAGE_TIERS.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
        </Form>
    );
};

export default BuildingComponentForm;
